// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
define(['uikit', 'lodash4', 'react', 'react-i18next', 'react-router-dom',
    '/www/lum/pub/styled.js', 'prop-types',
    '/www/lum/pub/constants.js', '/www/lum/pub/comp/ga.js',
    '/www/lum/pub/util/zmixpanel.js', '/www/lum/pub/uikit.util.js'],
    (uikit, _, React, {useTranslation}, {useLocation}, styled, PT, constants,
    ga, zmixpanel, uikit_util)=>
{
const {useState, useEffect, useCallback, useMemo} = React;
const {
    Layout, Typography, withPopover, Input, Menu, Icon, useToaster, useTheme,
} = uikit;
const {Flex} = Layout;
const {Label} = Typography;
const {Toggle, Dropdown} = Input;
const UIKitMenuItem = Menu.Item;
const {getCommonProps, getLinkProps, useLinkProps} = uikit_util;

const E = {...uikit, ...uikit_util, Toggle};

uikit.Tooltip.showDelay = 0.5;

const Link = React.forwardRef((props, ref)=>{
    const {text, children, variant, size, icon, iconPlacement, disabled,
      underline} = props;
    const linkProps = useLinkProps({...props, as: 'button'});
    const onClick = ga.hook_fn(disabled ? _.noop :
        props.onClick, 'zlink_click', props);
    const onMouseEnter = ga.hook_fn(props.onMouseEnter, 'zlink_hover', props);
    return <uikit.Link
      ref={ref}
      {...getCommonProps(props)}
      {...linkProps}
      text={text || children}
      variant={variant}
      size={size}
      icon={icon}
      iconPlacement={iconPlacement}
      disabled={disabled}
      underline={underline}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    />;
});
Link.displayName = 'CpLink';
Link.propTypes = {
    to: PT.oneOfType([PT.string, PT.object]),
    preserveQueryParams: PT.oneOfType([PT.bool, PT.object]),
};
E.Link = Link;

const Breadcrumbs = React.forwardRef(({items, ...rest}, ref)=>{
    const location = useLocation();
    const _items = useMemo(()=>items.map(item=>{
        const p = getLinkProps({...item, location});
        return {...item, ...p};
    }), [items]);
    return <uikit.Breadcrumbs ref={ref} items={_items} {...rest} />;
});
Breadcrumbs.displayName = 'CpBreadcrumbs';
E.Breadcrumbs = Breadcrumbs;

const MenuItem = React.forwardRef((props, ref)=>{
    const {text, title, ...rest} = props;
    const itemProps = _.omit(rest, 'match', 'location', 'history', 'to',
        'newTab', 'download', 'preserveQueryParams');
    const linkProps = useLinkProps(props);
    return <UIKitMenuItem
      ref={ref}
      {...itemProps}
      {...getCommonProps(props)}
      {...linkProps}
      text={text||title}
    />;
});
MenuItem.displayName = 'CpMenuItem';
Menu.Item = MenuItem;
E.Menu = Menu;

const Button = React.forwardRef((props, ref)=>{
    const {
        text, variant, size, icon, iconPlacement, disabled, loading,
        loadingText, onClick, onMouseEnter, type, form, children
    } = props;
    const linkProps = useLinkProps(props);
    const click = ga.hook_fn(disabled?_.noop:onClick, 'zbtn_click', props);
    const mouseEnter = ga.hook_fn(onMouseEnter, 'zbtn_hover', props);
    return <uikit.Button
      ref={ref}
      {...getCommonProps(props)}
      {...linkProps}
      type={type}
      form={form}
      text={text}
      variant={variant}
      size={size}
      icon={icon}
      iconPlacement={iconPlacement}
      disabled={disabled}
      loading={loading}
      loadingText={loadingText}
      onClick={click}
      onMouseEnter={mouseEnter}>
      {children}
    </uikit.Button>;
});
Button.displayName = 'CpButton';
Button.propTypes = {
    to: PT.oneOfType([PT.string, PT.object]),
    preserveQueryParams: PT.oneOfType([PT.bool, PT.object]),
    href: PT.string,
    newTab: PT.bool,
};
E.Button = Button;

const IconButton = React.forwardRef((props, ref)=>{
    const linkProps = useLinkProps(props);
    return <uikit.IconButton
      ref={ref}
      {...getCommonProps(props)}
      {...linkProps}
      type={props.type}
      tooltip={props.tooltip}
      variant={props.variant}
      tooltipPlacement={props.tooltipPlacement}
      icon={props.icon}
      disabled={props.disabled}
      active={props.active}
      size={props.size}
    />;
});
IconButton.displayName = 'CpIconButton';
E.IconButton = IconButton;

E.ButtonDropdown = React.forwardRef((props, ref)=>{
    const {items, icon = 'ChevronDown', comp, ...rest} = props;
    const [visible, set_visible] = useState(false);
    const Comp = comp=='icon' ? IconButton : Button;
    return <Popover
      trigger="click"
      placement="bottom"
      zIndex={999999}
      visible={visible}
      onVisibleChange={set_visible}
      popover={<Menu
        unstyled
        items={items}
        onClick={()=>set_visible(false)}
      />}>
      <Comp ref={ref} {...rest} icon={icon} iconPlacement="right" />
    </Popover>;
});
E.ButtonDropdown.displayName = 'ButtonDropdown';

const CodeBlock = props=>{
    const {title, docsLink=constants.LINK_API_DOCS, track_data,
        header: _header, copyButton = true, variant, extra = null,
        ...rest} = props;
    const {t} = useTranslation();
    const header = _header || <>
      <Label>{title}</Label>
      {docsLink&&<CodeBlockLink
        text={t('uikit.docs_link')} href={docsLink} $variant={variant}
      />}
      {extra}
    </>;
    const onCopy = useCallback(code=>{
        if (rest.onCopy&&typeof rest.onCopy == 'function')
            return rest.onCopy(code);
        if (_.isPlainObject(track_data)&&!_.isEmpty(track_data))
        {
            zmixpanel.track(`${track_data.name} code snippet copied`,
                track_data.payload);
        }
    }, [track_data, rest.onCopy]);
    return <StyledCodeBlock {...rest} variant={variant} header={header}
      copyButton={copyButton} onCopy={onCopy}/>;
};
E.CodeBlock = CodeBlock;

const CodeBlockDropdown = ({menuPortalTarget, menuMinHeight = '86px',
    menuMaxHeight, styles = {}, ...rest})=>{
    const t = useTheme();
    const _styles = {
        control: base_style=>({
            ...base_style,
            backgroundColor: 'inherit',
            border: '0',
            boxShadow: '0',
            color: 'inherit',
            padding: '0',
            minHeight: '0',
        }),
        singleValue: baseStyles=>({
            ...baseStyles,
            color: t.color.white,
            fontWeight: t.font_weight.semibold,
            fontSize: t.font_size.md,
        }),
        menuList: baseStyles=>({
            ...baseStyles,
            minHeight: menuMinHeight,
            maxHeight: menuMaxHeight || baseStyles.maxHeight,
        }),
        ...styles,
    };
    return <Dropdown
      menuPortalTarget={menuPortalTarget===null ? null : document.body}
      styles={_styles}
      {...rest}
    />;
};
E.CodeBlock.Dropdown = CodeBlockDropdown;

const StyledCodeBlock = styled(uikit.CodeBlock)`
    &&& {
        code[class*='language-'],
        pre[class*='language-'] {
            white-space: pre-wrap !important;
        }
    }
    pre {
        overflow: unset;
    }
    pre.line-numbers {
        padding-left: 3.8rem !important;
    }
`;

const CodeBlockLink = styled(Link).attrs({
    newTab: true,
    icon: 'ArrowUpRight',
    iconPlacement: 'right',
    variant: 'secondary',
})`
&& {
  [data-label] { 
    color: ${({$variant, theme: {color}})=>
      $variant=='white'?color.text_tertiary:color.text_white};
  }
  [data-icon] {
    color: ${({$variant, theme: {color}})=>
      $variant=='white'?color.icon:color.icon_white};
    width: 12px;
    height: 12px;
  }
}
`;

const Collapsible_section_btn = styled(Flex).attrs({
    as: 'button',
    padding: '0px',
})`
    background-color: transparent;
    border: none;
`;

const Collapsible_section = props=>{
    const {children, title, color, className} = props;
    const [show, setShow] = useState();
    const toggle = ()=>setShow(val=>!val);
    // XXX davidg: look recursively for children and don't render if
    // there aren't any
    return <Flex flex_direction="column" row_gap="05"
      className={className}>
      <Collapsible_section_btn onClick={toggle} column_gap="01">
        <Label color={color||'text_tertiary'} variant="sm">{title}</Label>
        <Icon name={show?'ChevronUp':'ChevronDown'} size="xs"
          color={color||'icon'}/>
      </Collapsible_section_btn>
      {show ? children : null}
    </Flex>;
};
E.Collapsible_section = Collapsible_section;

const ContextMenuButton = props=>{
    const {popover, icon = 'DotsVertical', ...rest} = props;
    const onClick = useCallback(evt=>{
        evt.stopPropagation();
        popover.toggle();
    }, [popover.toggle]);
    return <IconButton icon={icon} size="xs" {...rest}
      onClick={onClick} data-testid="context_menu_button"/>;
};

const ContextMenuPop = props=>{
    const {items, className, popover} = props;
    const hookHideAfterClick = cb=>(...args)=>{
        cb(...args);
        popover.hide();
    };
    const _items = items.map(i=>{
        if (i.onClick)
        {
            return Object.assign({}, i,
                {onClick: hookHideAfterClick(i.onClick)});
        }
        return i;
    });
    return <Menu items={_items} className={className}/>;
};
const ContextMenuComp = withPopover(ContextMenuButton, ContextMenuPop,
    {swallowOutsideClick: false});

const ContextMenu = props=>{
    const {items, menuClassName, className, icon, tooltip,
        onOpenChange, portalTarget} = props;
    const popoverProps = useMemo(()=>({
        items, className: menuClassName, portalTarget}),
        [items, menuClassName, portalTarget]);
    return <ContextMenuComp className={className} icon={icon} tooltip={tooltip}
      popoverProps={popoverProps} onOpenChange={onOpenChange}/>;
};
E.ContextMenu = ContextMenu;

const _toaster = {};
let _toasterInit = 0;
const toastTypes = ['success', 'error', 'warning', 'info', 'generic'];
['dismissToast', 'enqueueToast', 'getToast', ...toastTypes].forEach(fn=>{
  let tries = 0;
  _toaster[fn] = (...args)=>{
    setTimeout(()=>{
      if (tries>=3 && !_toasterInit)
      {
        console.log('toaster uninitialized', fn, args);
        return;
      }
      tries+=1;
      try {
        _toaster[fn](...args);
      } catch(e){
        console.log('toaster error', fn, args);
      }
    }, 1000);
  };
});
let toasterId = 0;
E.useToaster = ()=>{
  const toaster = useToaster();
  const {enqueueToast} = toaster;
  const [toasterState, setToasterState] = useState(_toaster);
  const _enqueueToast = useCallback((message, opt)=>{
    if (!opt.id||typeof opt.id!='string')
    {
        opt.id = String(toasterId);
        toasterId++;
    }
    const {type='generic'} = opt;
    const toastId = enqueueToast(message, opt);
    if (_toaster.ga)
    {
        let ga_message = _.isBoolean(message) || _.isString(message)
            || _.isNumber(message) || _.isDate(message) ? String(message) : '';
        if (toastId)
          ga_message = `(id: ${toastId}) ${ga_message}`;
        if (ga_message)
          _toaster.ga('alerts', type, ga_message);
    }
    return toastId;
  }, [enqueueToast]);
  useEffect(()=>{
    const {dismissToast, getToast} = toaster;
    const toast = {};
    toastTypes.forEach(type=>{
      toast[type] = (message, opt)=>_enqueueToast(message, {...opt, type});
    }, {});
      _toasterInit = !!toast;
    Object.assign(_toaster, toast,
      {dismissToast, enqueueToast: _enqueueToast, getToast});
    setToasterState(_toaster);
  }, [toaster]);
  return toasterState;
};
E.withToaster = Comp=>{
  const ToastedComponent = props=>{
    const toaster = E.useToaster();
    return <Comp {...props} toaster={toaster}/>;
  };
  return ToastedComponent;
};
const ToasterContent = ()=>{
    E.useToaster();
    return null;
};
class ToasterProvider extends React.Component {
    state = {error: null, errorInfo: null};
    static getDerivedStateFromError(error, errorInfo){
        return {error, errorInfo};
    }
    render(){
        return <uikit.Toaster {...this.props}>
          <ToasterContent />
        </uikit.Toaster>;
    }
}
E.ToasterProvider = ToasterProvider;
E.toaster = _toaster;

const Popover = ({
    visible,
    hideOnScroll = true,
    scrollTarget,
    onVisibleChange,
    ...rest
})=>{
    const [realVisible, setRealVisible] = useState(visible);
    useEffect(()=>setRealVisible(visible), [visible]);
    const handleVisibleChange = useCallback(value=>{
        setRealVisible(value);
        onVisibleChange?.(value);
    }, [onVisibleChange]);
    useEffect(()=>{
        if (!hideOnScroll || !realVisible)
            return;
        const scrollElem = scrollTarget
            || document.querySelector('[data-layout="page"]');
        if (scrollElem)
        {
            const handleScroll = ()=>handleVisibleChange(false);
            scrollElem.addEventListener('scroll', handleScroll);
            return ()=>scrollElem.removeEventListener('scroll', handleScroll);
        }
    }, [hideOnScroll, realVisible, scrollTarget, handleVisibleChange]);
    return <uikit.Popover
      {...rest}
      visible={realVisible}
      onVisibleChange={handleVisibleChange}
    />;
};
Popover.displayName = 'CpPopover';
E.Popover = Popover;

const Tooltip_old = ({noWrap, children, ...rest})=>{
    const content = noWrap ? children : <div>{children}</div>;
    return <uikit.Tooltip {...rest}>
      {content}
    </uikit.Tooltip>;
};
Tooltip_old.displayName = 'Tooltip_old';
E.Tooltip_old = Tooltip_old;

return E;
});
